import { useContext, useEffect, useState } from 'react';

import {
    Contract,
    CustomerTypeEnum,
    GetContractDealsByIdsInput,
    GetContractsByIdInput,
    GetEntitiesInput,
    GetRoyaltyModelTypesInput,
    RatchetModelsEnum,
    RateCard,
    RoyaltyModelsEnum,
    TerritoryTerm
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { sortBy } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { ServiceId, Status } from 'src/constants';
import { AppContext } from 'src/contexts/appContext';
import { IMetadata } from 'src/types/IMetadata';
import { IReportingRowData } from 'src/types/IReporting';
import { getConversionModel } from 'src/util/RateCards';
import { getGeneralStatus } from 'src/util/grid/status';

// Define supported service IDs (Prime and AMF) to filter data
const SUPPORTED_SERVICE_IDS = [ServiceId.Prime, ServiceId.Free];

export interface LoadingProgress {
    step: string;
    detail: string;
    percentage: number;
}

export const useReportingData = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [combinedData, setCombinedData] = useState<IReportingRowData[]>([]);
    const [loadingProgress, setLoadingProgress] = useState<LoadingProgress>({
        step: 'Initializing',
        detail: 'Starting data fetch',
        percentage: 0
    });
    const { setTopAlertMsg, metadata } = useContext(AppContext);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setIsLoading(true);

                // Update progress - Starting vendor data fetch
                setLoadingProgress({
                    step: 'Fetching Data',
                    detail: 'Loading vendor data...',
                    percentage: 10
                });

                // Fetch relevant APIs to augment metadata object
                const augmentedMetadata = await augmentMetadata();

                // Fetch vendors data
                const vendorsData = await fetchVendorsData();

                // Update progress - Starting deals data fetch
                setLoadingProgress({
                    step: 'Fetching Data',
                    detail: 'Loading deals data...',
                    percentage: 20
                });

                // Fetch deals data
                const dealsData = await fetchDealsData();

                // Combine vendors and deals data
                const combinedVendorsAndDeals = combineVendorsAndDeals(vendorsData, dealsData);

                // Extract unique contract IDs from deals data
                const contractIdsSet = new Set<number>();
                dealsData.forEach((deal) => {
                    if (deal.contractId) {
                        contractIdsSet.add(deal.contractId);
                    }
                });

                const contractIds = Array.from(contractIdsSet);

                // Fetch contract data for all contract IDs
                const coreRatesData = await fetchContractData(contractIds, setLoadingProgress, augmentedMetadata);

                // Update progress - Combining final data
                setLoadingProgress({
                    step: 'Processing',
                    detail: 'Combining core rates data...',
                    percentage: 80
                });

                // Combine all data
                const finalCombinedData = combineCoreRatesData(combinedVendorsAndDeals, coreRatesData);

                // Show 95% progress
                setLoadingProgress({
                    step: 'Finalizing',
                    detail: `Loaded ${finalCombinedData.length} rows successfully`,
                    percentage: 95
                });

                // Set the data
                setCombinedData(finalCombinedData);

                // Add a delay before showing 100% to ensure the 95% state is visible
                setTimeout(() => {
                    setLoadingProgress({
                        step: 'Complete',
                        detail: `Ready to display ${finalCombinedData.length} rows`,
                        percentage: 100
                    });

                    // Add another delay before hiding the loading screen
                    setTimeout(() => {
                        setIsLoading(false);
                    }, 500);
                }, 500);
            } catch (error) {
                setTopAlertMsg('Failed to load joint data view');
                setIsLoading(false);
            } finally {
                // Don't set isLoading to false here - we'll do it after the progress animation
                // setIsLoading(false);
            }
        };

        fetchAllData();
    }, []);

    // Call relevant APIs to augment metadata in AppContext
    const augmentMetadata = async () => {
        const royaltyModelTypesResponse = await contractConfigurationClient
            .getRoyaltyModelTypes(GetRoyaltyModelTypesInput.clone({}))
            .toPromise();

        return {
            ...metadata,
            royaltyModelTypes: royaltyModelTypesResponse.royaltyModelTypes ?? []
        };
    };

    const fetchVendorsData = async () => {
        try {
            const vendorsPromises = SUPPORTED_SERVICE_IDS.map((serviceId) => {
                return contractConfigurationClient
                    .getEntitiesByIds(
                        GetEntitiesInput.clone({ serviceIds: [serviceId], includeIneligibleVendorCodes: true })
                    )
                    .toPromise();
            });

            const responses = await Promise.all(vendorsPromises);

            // Transform vendor data to IReportingRowData format
            const vendorsData: IReportingRowData[] = [];

            responses.forEach((response) => {
                response.entities?.forEach((entity) => {
                    // Skip entities without vendor codes
                    if (!entity.entityVendorCodes || entity.entityVendorCodes.length === 0) return;

                    entity.entityVendorCodes.forEach((vendorCode) => {
                        if (!vendorCode.service || !vendorCode.territory) return;

                        vendorsData.push({
                            serviceId: vendorCode.service,
                            serviceName: metadata.serviceMapping[vendorCode.service]?.serviceName ?? '',
                            territoryId: vendorCode.territory,
                            territoryName: metadata.territoryMapping[vendorCode.territory]?.territoryCode ?? '',
                            entityId: entity.entityId,
                            entityName: entity.entityName,
                            catalogCode: vendorCode.catalogVendorCode,
                            royaltyCode: vendorCode.royaltyVendorCode
                        });
                    });
                });
            });

            return vendorsData;
        } catch (error) {
            console.error('Error fetching vendors data:', error);
            return [];
        }
    };

    const fetchDealsData = async () => {
        try {
            const response = await contractConfigurationClient
                .getContractDealsByIds(
                    GetContractDealsByIdsInput.clone({
                        includeIneligibleContractDeals: true,
                        serviceIds: SUPPORTED_SERVICE_IDS
                    })
                )
                .toPromise();

            // Transform deals data to IReportingRowData format
            const dealsData: IReportingRowData[] = [];

            response.contractDeals?.forEach((contractDeal) => {
                if (!contractDeal.entityActiveIntervals || contractDeal.entityActiveIntervals.length === 0) return;

                contractDeal.entityActiveIntervals.forEach((interval) => {
                    dealsData.push({
                        serviceId: contractDeal.serviceId,
                        serviceName: metadata.serviceMapping[contractDeal.serviceId]?.serviceName ?? '',
                        entityId: interval.entityId,
                        entityName: interval.entityName,
                        contractId: contractDeal.contractId,
                        contractName: contractDeal.contractName,
                        dealStartDate: interval.startDate,
                        dealEndDate: interval.endDate,
                        dealStatus: getGeneralStatus(interval)
                    });
                });
            });

            return dealsData;
        } catch (error) {
            console.error('Error fetching deals data:', error);
            return [];
        }
    };

    // Helper function to create a core rate data object
    const createCoreRateData = (
        contract: Contract,
        territoryTerm: TerritoryTerm,
        rateCard: RateCard,
        customerType: string,
        metadata: IMetadata
    ): IReportingRowData => {
        return {
            contractId: contract.contractId,
            contractName: contract.contractName,
            serviceId: contract.serviceId,
            serviceName: metadata.serviceMapping[contract.serviceId].serviceName ?? '',
            territoryId: territoryTerm.territoryId,
            territoryName: metadata.territoryMapping[territoryTerm.territoryId!].territoryCode ?? '',
            rateCardCurrency: rateCard.currency ?? '',
            settlementCurrency: territoryTerm.settlementCurrency ?? '',
            cae: territoryTerm.contentAcquisitionEntity?.contentAcquisitionEntityCode ?? '',
            playType: rateCard.playType?.playTypeText ?? '',
            playlistTypeDefinition:
                rateCard.playType?.playTypeDefinitions && rateCard.playType.playTypeDefinitions.length > 0
                    ? rateCard.playType.playTypeDefinitions[0].description
                    : '',
            customerType: customerType,
            programType: rateCard.programType?.programTypeName ?? '',
            capPrsDeductions: (() => {
                // Find the adjustment model with description "Label deduction cap"
                const labelDeductionCap = territoryTerm.adjustmentModels?.find(
                    (model) => model.description === 'Label deduction cap'
                );

                if (
                    labelDeductionCap?.adjustmentModelDefinitions &&
                    labelDeductionCap.adjustmentModelDefinitions.length > 0
                ) {
                    return labelDeductionCap.adjustmentModelDefinitions[0].value ?? '';
                }
                return '';
            })(),
            royaltyModelType: rateCard.royaltyModelType?.royaltyModelTypeId
                ? (metadata.royaltyModelTypes!.find(
                      (type) => type.royaltyModelTypeId === rateCard.royaltyModelType?.royaltyModelTypeId
                  )?.royaltyModelTypeText ?? '')
                : '',
            rate: rateCard.rate?.toString() ?? ''
        };
    };

    // Helper function to add additional data to core rate data object
    const addAdditionalDataToCoreRate = (
        coreRateData: IReportingRowData,
        contract: Contract,
        territoryTerm: TerritoryTerm,
        baseRateCard: RateCard
    ) => {
        // Add Ad Revenue Share Percentage only for AMF contracts with CPP royalty model
        if (
            contract.serviceId === ServiceId.Free &&
            baseRateCard.royaltyModel?.royaltyModelCode === RoyaltyModelsEnum.CPP
        ) {
            const adRevRateCard = territoryTerm.rateCards?.find(
                (rc) =>
                    rc.royaltyModel?.royaltyModelCode === RoyaltyModelsEnum.AD_REV &&
                    rc.playType?.playTypeText === baseRateCard.playType?.playTypeText
            );

            if (adRevRateCard && adRevRateCard.rate) {
                coreRateData.adRevenueSharePercentage = adRevRateCard.rate.toString();
            }
        }

        // Add ratchet model information if available
        if (baseRateCard.ratchetModels && baseRateCard.ratchetModels.length > 0) {
            // Use the orderId=1 or first ratchet model
            const firstRatchetModel =
                baseRateCard.ratchetModels.find((model) => model.orderId === 1) || baseRateCard.ratchetModels[0];

            if (firstRatchetModel) {
                // Add ratchet model description
                coreRateData.ratchetModelName = firstRatchetModel.description ?? '';

                // Get conversion model
                const conversionModel = getConversionModel([firstRatchetModel]);

                if (conversionModel) {
                    sortBy(conversionModel.ratchets ?? [], ['threshold']).forEach((ratchet, index) => {
                        coreRateData[`threshold${index + 1}`] = ratchet.rate?.toString() || '';
                    });
                }
            }

            const wmgTertiaryRatchetModel = baseRateCard.ratchetModels.find(
                (ratchetModel) => ratchetModel.ratchetModel === RatchetModelsEnum.PRIME_TERTIARY_CONVERSION_WMG
            );

            if (wmgTertiaryRatchetModel) {
                sortBy(wmgTertiaryRatchetModel.ratchets ?? [], ['threshold']).forEach((ratchet, index) => {
                    coreRateData[`blended${index + 1}`] = ratchet.rate?.toString() || '';
                });
            }
        }

        return coreRateData;
    };

    const fetchContractData = async (
        contractIds: number[],
        setProgress: (progress: LoadingProgress) => void,
        augmentedMetadata: IMetadata
    ) => {
        try {
            const coreRatesData: IReportingRowData[] = [];

            // Process contracts in batches
            const batchSize = 5;
            const totalBatches = Math.ceil(contractIds.length / batchSize);

            for (let i = 0; i < contractIds.length; i += batchSize) {
                const currentBatch = Math.floor(i / batchSize) + 1;
                const batchStart = i;
                const batchEnd = Math.min(i + batchSize, contractIds.length);

                // Update progress for this batch
                const progressPercentage = 40 + Math.floor((currentBatch / totalBatches) * 40); // 40% to 80%
                setProgress({
                    step: 'Fetching Data',
                    detail: `Loading contracts ${batchStart + 1}-${batchEnd} of ${contractIds.length}`,
                    percentage: progressPercentage
                });

                const batch = contractIds.slice(i, i + batchSize);
                const batchPromises = batch.map((contractId) => {
                    return contractConfigurationClient
                        .getContractsById(
                            GetContractsByIdInput.clone({
                                contractId: contractId,
                                excludeRoyaltyModelTypes: true,
                                royaltyModelCodes: [
                                    RoyaltyModelsEnum.MAU,
                                    RoyaltyModelsEnum.CPP,
                                    RoyaltyModelsEnum.AD_REV
                                ],
                                includeIneligibleContractDeals: true,
                                excludeTerritoryTerms: false
                            })
                        )
                        .toPromise()
                        .then((contractResponse) => {
                            const contract = contractResponse.contracts?.[0];
                            if (!contract) return [];

                            const contractRatesData: IReportingRowData[] = [];

                            contract.territoryTerms?.forEach((territoryTerm) => {
                                // Group rate cards by play type and program type
                                const cppRateCardsByPlayTypeAndProgram: Record<string, RateCard[]> = {};
                                const mauRateCardsByPlayTypeAndProgram: Record<string, RateCard[]> = {};
                                const mauFreeTrialRateCards: RateCard[] = [];

                                // Separate rate cards into different categories
                                territoryTerm.rateCards?.forEach((rateCard: RateCard) => {
                                    // Skip ADREV rate cards
                                    if (rateCard.royaltyModel?.royaltyModelCode === RoyaltyModelsEnum.AD_REV) {
                                        return;
                                    }

                                    // For Prime MAU rateCards
                                    if (
                                        contract.serviceId === ServiceId.Prime &&
                                        rateCard.royaltyModel?.royaltyModelCode === RoyaltyModelsEnum.MAU
                                    ) {
                                        // Handle FREE_TRIAL rate cards separately
                                        if (rateCard.paperPlanName === CustomerTypeEnum.FREE_TRIAL) {
                                            mauFreeTrialRateCards.push(rateCard);
                                            return;
                                        }
                                        // Group other MAU rate cards by play type and program type
                                        else {
                                            const key = `${rateCard.playType?.playTypeText || ''}-${rateCard.programType?.programTypeName || ''}`;
                                            if (!mauRateCardsByPlayTypeAndProgram[key]) {
                                                mauRateCardsByPlayTypeAndProgram[key] = [];
                                            }
                                            mauRateCardsByPlayTypeAndProgram[key].push(rateCard);
                                            return;
                                        }
                                    }

                                    // Group CPP rate cards by play type and program type for both Prime and AMF
                                    if (rateCard.royaltyModel?.royaltyModelCode === RoyaltyModelsEnum.CPP) {
                                        const key = `${rateCard.playType?.playTypeText || ''}-${rateCard.programType?.programTypeName || ''}`;
                                        if (!cppRateCardsByPlayTypeAndProgram[key]) {
                                            cppRateCardsByPlayTypeAndProgram[key] = [];
                                        }
                                        cppRateCardsByPlayTypeAndProgram[key].push(rateCard);
                                    }
                                });

                                // Process CPP rate cards
                                Object.values(cppRateCardsByPlayTypeAndProgram).forEach((rateCards) => {
                                    if (rateCards.length === 0) return;

                                    // Use the first rate card as the base
                                    const baseRateCard = rateCards[0];

                                    // Determine customer type based on service
                                    let customerType = '';
                                    if (contract.serviceId === ServiceId.Prime) {
                                        // For Prime CPP, combine customer types
                                        customerType = rateCards
                                            .map((rc) => rc.paperPlanName)
                                            .sort()
                                            .join(' | ');
                                    }
                                    // For AMF CPP, leave customer type empty (already default '')

                                    // Create core rate data
                                    const coreRateData = createCoreRateData(
                                        contract,
                                        territoryTerm,
                                        baseRateCard,
                                        customerType,
                                        augmentedMetadata
                                    );

                                    // Add additional data (ad revenue share, ratchet models)
                                    addAdditionalDataToCoreRate(coreRateData, contract, territoryTerm, baseRateCard);

                                    contractRatesData.push(coreRateData);
                                });

                                // Process MAU rate cards
                                if (contract.serviceId === ServiceId.Prime) {
                                    Object.values(mauRateCardsByPlayTypeAndProgram).forEach((rateCards) => {
                                        if (rateCards.length === 0) return;

                                        // Use the first rate card as the base
                                        const baseRateCard = rateCards[0];

                                        // Combine customer types for Prime MAU
                                        const customerType = rateCards
                                            .map((rc) => rc.paperPlanName)
                                            .sort()
                                            .join(' | ');

                                        // Create core rate data
                                        const coreRateData = createCoreRateData(
                                            contract,
                                            territoryTerm,
                                            baseRateCard,
                                            customerType,
                                            augmentedMetadata
                                        );

                                        // Add additional data
                                        addAdditionalDataToCoreRate(
                                            coreRateData,
                                            contract,
                                            territoryTerm,
                                            baseRateCard
                                        );

                                        contractRatesData.push(coreRateData);
                                    });

                                    // Process MAU FREE_TRIAL rate cards separately
                                    mauFreeTrialRateCards.forEach((rateCard) => {
                                        const coreRateData = createCoreRateData(
                                            contract,
                                            territoryTerm,
                                            rateCard,
                                            rateCard.paperPlanName ?? '',
                                            augmentedMetadata
                                        );

                                        // Add additional data
                                        addAdditionalDataToCoreRate(coreRateData, contract, territoryTerm, rateCard);

                                        contractRatesData.push(coreRateData);
                                    });
                                }
                            });

                            return contractRatesData;
                        })
                        .catch((error) => {
                            console.error(`Error fetching data for contract ${contractId}:`, error);
                            return [];
                        });
                });

                const batchResults = await Promise.all(batchPromises);

                batchResults.forEach((contractRates) => {
                    coreRatesData.push(...contractRates);
                });
            }

            return coreRatesData;
        } catch (error) {
            console.error('Error fetching contract data:', error);
            return [];
        }
    };

    const combineVendorsAndDeals = (
        vendorsData: IReportingRowData[],
        dealsData: IReportingRowData[]
    ): IReportingRowData[] => {
        // Create a map of deals by service and entity ID
        const dealsByServiceAndEntity: Record<string, IReportingRowData[]> = {};

        dealsData.forEach((deal) => {
            if (!deal.serviceId || !deal.entityId || deal.dealStatus !== Status.Active) return;

            const key = `${deal.serviceId}-${deal.entityId}`;
            if (!dealsByServiceAndEntity[key]) {
                dealsByServiceAndEntity[key] = [];
            }
            dealsByServiceAndEntity[key].push(deal);
        });

        // Create the result array directly from vendors that have matching deals
        const result: IReportingRowData[] = [];

        // Only include vendors that have matching deals
        vendorsData.forEach((vendor) => {
            if (!vendor.serviceId || !vendor.entityId || !vendor.territoryId) return;

            const key = `${vendor.serviceId}-${vendor.entityId}`;
            const deals = dealsByServiceAndEntity[key];

            // Skip vendors without deals
            if (!deals || deals.length === 0) return;

            const deal = deals[0];

            // Create a combined record
            result.push({
                // Deal data first
                contractId: deal.contractId,
                contractName: deal.contractName,
                dealStartDate: deal.dealStartDate,
                dealEndDate: deal.dealEndDate,
                dealStatus: deal.dealStatus,

                // Vendor data
                serviceId: vendor.serviceId,
                serviceName: vendor.serviceName,
                entityId: vendor.entityId,
                entityName: vendor.entityName,
                territoryId: vendor.territoryId,
                territoryName: vendor.territoryName,
                catalogCode: vendor.catalogCode,
                royaltyCode: vendor.royaltyCode
            });
        });

        return result;
    };

    const combineCoreRatesData = (
        combinedData: IReportingRowData[],
        coreRatesData: IReportingRowData[]
    ): IReportingRowData[] => {
        // Create a map of core rates by contract, territory, play type, and program type
        const coreRatesMap: Record<string, IReportingRowData> = {};

        coreRatesData.forEach((rate) => {
            if (!rate.contractId || !rate.territoryId) return;

            // Create a unique key that includes play type, program type and customer type
            const key = `${rate.contractId}-${rate.territoryId}-${rate.playType || ''}-${rate.programType || ''}-${rate.customerType || ''}`;
            coreRatesMap[key] = rate;
        });

        const result: IReportingRowData[] = [];

        // For each combined vendor-deal record
        combinedData.forEach((record) => {
            const matchingRates: IReportingRowData[] = [];

            // Check all possible combinations in the core rates map
            Object.keys(coreRatesMap).forEach((key) => {
                if (key.startsWith(`${record.contractId}-${record.territoryId}-`)) {
                    matchingRates.push(coreRatesMap[key]);
                }
            });

            if (matchingRates.length === 0) {
                // If no matching core rates, just add the record as is
                result.push(record);
            } else {
                // For each matching core rate, create a combined record
                matchingRates.forEach((coreRate) => {
                    const combinedRecord: IReportingRowData = {
                        ...record,
                        rateCardCurrency: coreRate.rateCardCurrency,
                        settlementCurrency: coreRate.settlementCurrency,
                        cae: coreRate.cae,
                        playType: coreRate.playType,
                        playlistTypeDefinition: coreRate.playlistTypeDefinition,
                        customerType: coreRate.customerType,
                        programType: coreRate.programType,
                        capPrsDeductions: coreRate.capPrsDeductions,
                        rate: coreRate.rate,
                        royaltyModelType: coreRate.royaltyModelType,
                        adRevenueSharePercentage: coreRate.adRevenueSharePercentage,
                        ratchetModelName: coreRate.ratchetModelName,
                        threshold1: coreRate.threshold1,
                        threshold2: coreRate.threshold2,
                        threshold3: coreRate.threshold3,
                        threshold4: coreRate.threshold4,
                        blended1: coreRate.blended1,
                        blended2: coreRate.blended2,
                        blended3: coreRate.blended3,
                        blended4: coreRate.blended4
                    };

                    result.push(combinedRecord);
                });
            }
        });

        return result;
    };

    return { isLoading, combinedData, loadingProgress };
};
